<template>
  <div
    v-if="isShowModal"
    class="new-child"
  >
    <span class="new-child__close" @click="closeModal"></span>
    <h2 class="new-child__title">Missing information for user {{ user.full_name }}</h2>

    <div
      v-if="user.ageIsEmpty"
    >
      <!--DoB-->
      <label
        class="form__row"
      >
        <span class="form__row-label">Birth Date</span>
        <date-picker
          v-model="birth_date"
          input-class="input-date"
          valueType="format"
          placeholder="Select"
          :disabled-date="(date) => date >= new Date()"
          :class="{'is-invalid': $v.birth_date && $v.birth_date.$dirty && !$v.birth_date.required}"
        ></date-picker>
      </label>

    </div>

    <div
      v-for="(field, index) in user.emptyDynamicFields"
      :key="'emptyDynamicField' + index"
    >
      <DynamicFieldRow
        :key="field.id"
        :fields-data="field"
      />
    </div>

    <p class="form__error">{{errorMessage}}</p>

    <p
      v-if="$v.birth_date.$invalid && $v.birth_date.$dirty"
      class="form__error"
    >
      Please complete required fields
    </p>

    <button
      class="btn btn--color-dark form__button"
      @click="saveChild"
      :disabled="isLoading"
    >
      Save
    </button>
  </div>
</template>

<script>
import { instanceSessionCamperNames } from '@core/mixins/instanceSessionCamperNames'

import DatePicker from 'vue2-datepicker'
import 'vue2-datepicker/index.css'

import { required, helpers } from 'vuelidate/lib/validators'
import store from '@/store'
import DynamicFieldRow from "@/components/site/add-new-child-modal/DynamicFieldRow.vue";

  export default {
    components: {
      DynamicFieldRow,
      DatePicker,
    },
    props: {
      user: {
        type: Object,
        required: true,
      },
      isShowModal: {
        type: Boolean,
        default: false,
      },
    },
    mixins: [instanceSessionCamperNames],
    data(){
      return {
        birth_date: null,
      }
    },
    validations() {
      return {
        birth_date: {
          required: helpers.withParams(
            { type: 'requiredIfAgeIsEmpty' },
            function () {
              return this.user.ageIsEmpty ? required : helpers.req(true)
            }
          )
        }
      }
    },
    watch: {
      isShowModal(val) {
        if (val) {
          this.resetModalData()
        }
      },
    },
    computed: {
      isLoading: {
        get: function () {
          return store.getters['addNewChild/getIsLoading']
        },
        set: function (val) {
          store.commit('addNewChild/SET_IS_LOADING', val)
        }
      },
      errorMessage: {
        get: function () {
          return store.getters['addNewChild/getErrorMessage']
        },
        set: function (val) {
          store.commit('addNewChild/SET_ERROR_MESSAGE', val)
        }
      },
    },
    methods: {
      resetModalData() {
        this.birth_date = null
      },
      closeModal() {
        this.resetModalData()
        this.$emit('closeModal')
      },
      async saveChild() {
        await this.$nextTick()

        await this.$v.$touch()
        if (this.$v.$invalid){
          return
        }

        const queryParams = {}

        if (this.user.ageIsEmpty) {
          queryParams.extra_user_info = {
            birth_date: this.birth_date,
          }
        }

        if (this.user.emptyDynamicFields.length > 0) {
          queryParams.dynamic_profile_fields = Object.values(this.user.emptyDynamicFields)
            .map(field => {
              return {
                name: field.name,
                label: field.label,
                value: field.value,
                original_id: field.id,
              }
            })
        }

        this.isLoading = true
        await store.dispatch('addNewChild/updateChildInfo', {
          user_id: this.user.id,
          query: queryParams,
        })
          .then(() => {
            this.$emit('updateChildrenList')
            this.isLoading = false
            this.closeModal()
          })
          .catch(error => {
            if (error.response.status === 422) {
              this.isLoading = false
              const validationErrors = Object.values(error.response.data.errors)
              this.errorMessage = validationErrors.flat()
            }
          })
        },
    }
  }
</script>

<style lang="scss" scoped>
@import 'src/assets/scss/colors/colors';

.new-child {
  position: absolute;
  border-radius: 10px;
  padding: 50px;
  width: 500px;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -20%);
  background: #FFF;
  z-index: 20;
  box-shadow:  0px 0px 0px 9999px rgba(0, 0, 0, 0.5);

  &__close {
    position: absolute;
    display: flex;
    align-items: center;
    top: 20px;
    right: 20px;
    width: 15px;
    height: 15px;
    cursor: pointer;

    &::before,&::after {
      content: '';
      display: block;
      position: absolute;
      left: 6px;
      width: 1px;
      height: 15px;
      background: #000;
      transform: rotate(45deg);
    }
    &::after{
      transform: rotate(-45deg);
    }
  }
  &__title {
    font-size: 24px;
    font-weight: 700;
    text-align: center;
    margin-bottom: 30px;
  }

  &__avatar {
    display: flex;
    flex-direction: column;
    align-items: center;
    &-img {
      background: #EDEDED;
      width: 80px;
      height: 80px;
      border-radius: 50%;
      margin-bottom: 10px;
      img {
        object-fit: cover;
        width: 100%;
        height: 100%;
        border-radius: 50%;
      }
    }

    &-btn {
      cursor: pointer;
      display: flex;
      align-items: center;
      color: $primary;
      svg > g > path {
        stroke: $primary;
      }
      span {
        margin-left: 5px;
      }
    }
  }
}
@media screen and (max-width: 767px) {
  .new-child {
    position: absolute;
    border-radius: 10px;
    padding: 15px;
    width: 320px;
    left: 50%;
    top: 50%;
    background: #FFF;
    z-index: 20;
    box-shadow:  0px 0px 0px 9999px rgba(0, 0, 0, 0.5);
  }
}
</style>
<style lang="scss">
.mx-datepicker{
  &.is-invalid{
    .input-date {
      border: 1px solid var(--error) !important;
      &::placeholder {
        color: var(--error)
      }
    } 
  }  
}
.new-child__avatar-img.is-invalid {
  border: 1px solid var(--error) !important;
  border-color: var(--error);
}
</style>